import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, XClose } from '@untitled-ui/icons-react';
import { useEffect } from 'react';

import { Button } from '../../../components/buttons/button';
import { IconButton } from '../../../components/buttons/icon-button';
import { EditorSection } from '../../../components/editor/editor-section';
import { ProductSidebar } from '../../../components/editor/sidebar';
import {
  StickyElement,
  StickyLayout,
  StickyShell,
} from '../../../components/layouts/sticky-layout';
import { setCustomMetaTitle } from '../../../components/meta-title';
import { MobileBlocker } from '../../../components/mobile-blocker';
import { ExportKind } from '../../../entity/api/export';
import { MediaKind } from '../../../entity/api/media';
import { PlanFeature } from '../../../entity/api/plan';
import { type Product, ProductStatus } from '../../../entity/api/product';
import { type ProductSection } from '../../../entity/api/product-section';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { getQueryClient } from '../../../sdk/request';
import {
  fetchMediaByRefDocIdImperative,
  fetchProductByIdImperative,
  getAdjacentProductIdFromCache,
  getOrganizationBillingFromCache,
  useExportProducts,
  useMediaByRefDocId,
  useProductById,
  useProductSectionsByProductId,
  useUpdateProduct,
} from '../../../services/storage';
import { getAuthUserFromCache } from '../../../services/storage/users-queries';
import { tGlobal, tNamespace } from '../../../services/translation';
import { getSelectedBrand } from '../../../shared/brand';
import { Feature, isFeatureAvailable } from '../../../shared/feature';
import { getSelectedOrganization } from '../../../shared/organization';
import { hasOrgFeature, isOrgCancelled } from '../../../shared/subscription';

const PARENT_ROUTE = '/app/products';

const t = tNamespace('app.product.$id');

export const Route = createFileRoute('/app/product/$id')({
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
  beforeLoad: async function (args) {
    const { params } = args;
    const queryClient = getQueryClient();
    const currentOrg = getSelectedOrganization(getAuthUserFromCache(queryClient));
    const orgBilling = getOrganizationBillingFromCache(queryClient, currentOrg?._id ?? '');

    // we require an organization with the right permissions, send to the parent if not
    if (isOrgCancelled(orgBilling) || !hasOrgFeature(orgBilling, PlanFeature.EnabledProducts)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: PARENT_ROUTE,
      });
    }

    return Promise.allSettled([
      fetchProductByIdImperative(queryClient, params.id),
      fetchMediaByRefDocIdImperative(queryClient, params.id),
    ]).catch(function (err) {
      captureException(
        err,
        {
          file: 'src/routes/app/product/$id.tsx',
          method: 'beforeLoad',
          infoMessage: 'unable to fetch data from api',
        },
        trackError
      );
    });
  },
});

function RouteComponent() {
  const { id: productId }: { id: string } = Route.useParams();
  const navigate = useNavigate();

  const queryClient = getQueryClient();

  // TanStack Query hook for auth user
  const currentUser = getAuthUserFromCache(queryClient);
  // TanStack Query hooks
  const { data: product } = useProductById(productId);
  const { data: productSections = [] } = useProductSectionsByProductId(productId);

  const currentBrand = getSelectedBrand(currentUser);

  // Get adjacent product IDs from cache
  const nextId = getAdjacentProductIdFromCache(
    queryClient,
    'next',
    productId,
    currentBrand?._id ?? ''
  );
  const previousId = getAdjacentProductIdFromCache(
    queryClient,
    'prev',
    productId,
    currentBrand?._id ?? ''
  );

  // Media from TanStack Query
  const { data: mediaList = [] } = useMediaByRefDocId(productId);
  const productImages = mediaList
    .filter(function (media) {
      return media.kind === MediaKind.ProductImage;
    })
    .map(function (media) {
      return media.url;
    });

  // TanStack Query mutations
  const updateProductMutation = useUpdateProduct();
  const exportProductsMutation = useExportProducts();

  function updateSectionBody(id: string, bodyDoc: string) {
    if (product == null) {
      return;
    }
    const newProduct: Product = { ...product };
    newProduct.createdBy = currentUser?._id ?? newProduct.createdBy;
    const sections = productSections.map(function (section) {
      if (section._id !== id) {
        return section;
      }
      return { ...section, bodyDoc };
    });

    updateProductMutation.mutate(
      { product: newProduct, sections },
      {
        onError: function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/product/$id.tsx',
              method: 'updateSectionBody',
              infoMessage: 'unable to update product section',
            },
            trackError
          );
        },
      }
    );
  }

  useEffect(
    function () {
      if (product == null) {
        return;
      }
      setCustomMetaTitle(product.title, false);
      if (
        product.status === ProductStatus.GenerationError ||
        product.status === ProductStatus.Generating
      ) {
        navigate({ to: PARENT_ROUTE }).catch(function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/product/$id.tsx',
              method: 'navigate',
              infoMessage: 'unable to navigate to /app/products',
            },
            trackError
          );
        });
      }
    },
    [product, navigate]
  );

  useEffect(
    function () {
      if (!isFeatureAvailable(Feature.PRODUCT)) {
        navigate({ to: '/app/blogs' }).catch(function (err) {
          captureException(
            err,
            {
              file: 'src/routes/app/product/$id.tsx',
              method: 'navigate',
              infoMessage: 'unable to navigate to /app/blogs',
            },
            trackError
          );
        });
      }
    },
    [navigate]
  );

  if (product == null) {
    return null;
  }

  const sortedSections: ProductSection[] = Array.isArray(productSections)
    ? [...productSections].sort(function (a, b) {
        return a.order - b.order;
      })
    : [];

  return (
    <>
      <MobileBlocker />
      <StickyLayout
        header={
          <StickyElement
            kind="header"
            className="grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4"
          >
            <IconButton
              icon={XClose}
              variant="outline"
              onClick={function () {
                navigate({ to: PARENT_ROUTE }).catch(function (err) {
                  captureException(
                    err,
                    {
                      file: 'src/routes/app/product/$id.tsx',
                      method: 'navigate',
                      infoMessage: 'unable to navigate to /app/products',
                    },
                    trackError
                  );
                });
              }}
            />
            <h4 className="font-display-4">{t('reviewEditProduct')}</h4>
            <IconButton
              icon={ArrowLeft}
              variant="outline"
              disabled={previousId == null}
              onClick={function () {
                if (previousId == null) {
                  return;
                }
                navigate({ to: `/app/product/${previousId}`, params: { id: previousId } }).catch(
                  function (err) {
                    captureException(
                      err,
                      {
                        file: 'src/routes/app/product/$id.tsx',
                        method: 'navigate to previous',
                        infoMessage: `unable to navigate to /app/products/${previousId}`,
                      },
                      trackError
                    );
                  }
                );
              }}
            />
            <IconButton
              icon={ArrowRight}
              variant="outline"
              disabled={nextId == null}
              onClick={function () {
                if (nextId == null) {
                  return;
                }
                navigate({ to: `/app/product/${nextId}`, params: { id: nextId } }).catch(
                  function (err) {
                    captureException(
                      err,
                      {
                        file: 'src/routes/app/product/$id.tsx',
                        method: 'navigate',
                        infoMessage: 'unable to navigate to /app/products',
                      },
                      trackError
                    );
                  }
                );
              }}
            />
            <Button
              onClick={function () {
                exportProductsMutation.mutate(
                  { ids: [productId], kind: ExportKind.CSV, productName: product?.title ?? '' },
                  {
                    onError: function (err) {
                      captureException(
                        err,
                        {
                          file: 'src/routes/app/product/$id.tsx',
                          method: 'export',
                          infoMessage: 'unable to export to csv',
                        },
                        trackError
                      );
                    },
                  }
                );
              }}
            >
              {tGlobal('export')}
            </Button>
          </StickyElement>
        }
        body={
          <StickyShell className="flex h-full overflow-hidden" key={product?._id}>
            <ProductSidebar product={product} productImages={productImages} />
            <div className="w-full overflow-auto">
              <div className="flex-1 py-6">
                <div className="mx-auto grid w-full max-w-content grid-flow-row grid-cols-1 gap-7">
                  <h4
                    key="generated-content"
                    className="font-display-4 bg-gradient-to-r from-branding-accent-600 to-purple-900 bg-clip-text text-transparent"
                  >
                    {t('generatedContent')}
                  </h4>
                  {sortedSections.map(function (section) {
                    if (typeof product?._id !== 'string' || product._id.trim().length === 0) {
                      return null;
                    }
                    return (
                      <EditorSection
                        key={section._id}
                        name={section.title}
                        currentUser={currentUser}
                        contentId={product?._id}
                        value={section.bodyDoc}
                        wordLimit={section.maxNumberOfWords}
                        onUpdate={function (jsonString) {
                          updateSectionBody(section._id, jsonString);
                        }}
                        productId={product._id}
                        productSectionId={section._id}
                        organizationId={product.organizationId}
                        brandId={product.brandId}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </StickyShell>
        }
      />
    </>
  );
}
